import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, type ReactNode } from 'react'
import { AuthProvider, useAuth } from './auth'
import type { Role } from './api'
import Login from './pages/Login'
import Home from './pages/Home'
import StaffHome from './pages/StaffHome'
import QuickBill from './pages/QuickBill'
import Doctors from './pages/Doctors'
import Settings from './pages/Settings'
import Users from './pages/Users'
import ChangePassword from './pages/ChangePassword'
import Reception from './pages/Reception'
import VisitPage from './pages/Visit'
import Console from './pages/Console'
import Appointments from './pages/Appointments'
import Records from './pages/Records'
import Reports from './pages/Reports'
import Admin from './pages/Admin'
import Investigations from './pages/Investigations'
import Billing from './pages/Billing'
import Mobile from './pages/mobile'
import MobileLogin from './pages/mobile/Login'
import PlatformLayout from './pages/platform/PlatformLayout'
import DeskShell from './components/DeskShell'

/** Every staff screen sits inside the desktop shell (sidebar, top bar, account menu). */
function Shell({ children }: { children: ReactNode }) {
  return <DeskShell>{children}</DeskShell>
}

/** Everyone who works at the clinic - that is, everyone except a patient. */
const STAFF: Role[] = ['admin', 'doctor', 'receptionist']

function Protected({ children, roles, bare }: { children: ReactNode; roles?: Role[]; bare?: boolean }) {
  const { user, loading } = useAuth()

  // `bare` is the mobile app. It keeps its own sign-in and its own password
  // screen, so an unauthenticated phone user must not be thrown into the
  // desktop chrome on the way past.
  const loginPath = bare ? '/m/login' : '/login'
  const changePath = bare ? '/m/change-password' : '/change-password'

  if (loading) return <div className="loading">Loading&hellip;</div>
  if (!user) return <Navigate to={loginPath} replace />

  // A temporary password must be changed before anything else is reachable.
  if (user.mustChangePassword && window.location.pathname !== changePath) {
    return <Navigate to={changePath} replace />
  }

  // A platform admin belongs to no clinic: every clinic screen, desktop or
  // phone, would only fill with 403s, so it sends them to the platform screen.
  if (user.role === 'platform_admin' && !roles?.includes('platform_admin') && window.location.pathname !== changePath) {
    return <Navigate to="/platform" replace />
  }

  // A patient has no desktop screens - the whole patient experience is /m.
  // Without this they land in the staff chrome and every panel fills with
  // 403s, or worse renders an empty queue that reads as "no visits today".
  if (!bare && (user.role === 'patient' || user.role === 'lab_manager')) {
    return <Navigate to="/m" replace />
  }
  if (roles && !roles.includes(user.role)) {
    return (
      <Shell>
        <div className="page-head"><h2>Not available</h2></div>
        <div className="msg error">Your role does not have access to this screen.</div>
      </Shell>
    )
  }
  // `bare` screens bring their own chrome - the mobile app page has its own
  // header and tab bar, so the desktop sidebar would only get in the way.
  return bare ? <>{children}</> : <Shell>{children}</Shell>
}

/**
 * `/` is the staff dashboard once you are signed in. Signed out, it is the public
 * home page (with the app download) - except inside the Android app, which goes
 * straight to sign-in like it always did.
 */
function RootRoute() {
  const { user, loading } = useAuth()
  if (loading) return <div className="loading">Loading&hellip;</div>
  if (!user) {
    return isNativeApp() ? <Navigate to="/m/login" replace /> : <Home />
  }
  return <Protected roles={STAFF}><StaffHome /></Protected>
}

/** True inside the Android app (Capacitor), false in any browser. */
function isNativeApp(): boolean {
  return !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()
}

/**
 * The Android app is the phone app: every screen in it is the mobile one.
 * It loads the same site as a browser, so without this it opened the desktop
 * sign-in and then the desktop dashboard. Any address outside /m - the start
 * page, the desktop sign-in, a desktop link - goes to its mobile home instead.
 * The platform console stays where it is, for the operator.
 */
function NativeToMobile({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const { user, loading } = useAuth()
  if (!isNativeApp() || pathname.startsWith('/m') || pathname.startsWith('/platform')) return <>{children}</>
  if (loading) return <div className="loading">Loading&hellip;</div>
  if (user?.role === 'platform_admin') return <>{children}</>
  return <Navigate to={user ? '/m' : '/m/login'} replace />
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NativeToMobile>
        <Routes>
          {/* Staff screens name their roles explicitly rather than relying on
              the patient redirect above, so the guard survives a refactor and
              the server's 403s stay a backstop rather than the only defence. */}
          <Route path="/login" element={<Login />} />
          <Route path="/platform/*" element={<Protected roles={['platform_admin']} bare><PlatformLayout /></Protected>} />
          <Route path="/" element={<RootRoute />} />
          <Route path="/overview" element={<Navigate to="/" replace />} />
          <Route path="/bill/new" element={<Protected roles={STAFF}><QuickBill /></Protected>} />
          <Route path="/reception" element={<Protected roles={STAFF}><Reception /></Protected>} />
          <Route path="/console" element={<Protected roles={['admin', 'doctor']}><Console /></Protected>} />
          <Route path="/appointments" element={<Protected roles={STAFF}><Appointments /></Protected>} />
          <Route path="/records" element={<Protected roles={STAFF}><Records /></Protected>} />
          <Route path="/reports" element={<Protected roles={['admin', 'doctor']}><Reports /></Protected>} />
          <Route path="/admin" element={<Protected roles={['admin']}><Admin /></Protected>} />
          <Route path="/investigations" element={<Protected roles={STAFF}><Investigations /></Protected>} />
          <Route path="/billing" element={<Protected roles={STAFF}><Billing /></Protected>} />
          <Route path="/m/login" element={<MobileLogin />} />
          <Route path="/m/*" element={<Protected bare><Mobile /></Protected>} />
          <Route path="/visits/:id" element={<Protected roles={STAFF}><VisitPage /></Protected>} />
          <Route path="/doctors" element={<Protected roles={['admin']}><Doctors /></Protected>} />
          <Route path="/users" element={<Protected roles={['admin']}><Users /></Protected>} />
          <Route path="/settings" element={<Protected roles={['admin']}><Settings /></Protected>} />
          <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </NativeToMobile>
      </BrowserRouter>
    </AuthProvider>
  )
}
